import _ from 'lodash';
import * as platformEvents from 'platformEvents';
import { arrayUtils } from '#packages/util';
import {
  interactionsActions,
  interactionsSelectors,
  selectionSelectors,
} from '#packages/stateManagement';
import * as componentsUtils from './componentsUtils';
import experiment from 'experiment';

import type {
  DSRead,
  CompRef,
  CompStructure,
  CompVariantPointer,
  StyleRefOrStyleRefs,
  StyleRef,
} from 'types/documentServices';
import type { EditorAPI } from '#packages/editorAPI';
import { hasResponsiveLayout } from './componentsStylableApi/componentsStylableApi';

export function createComponentsStyleApi({
  editorAPI,
}: {
  editorAPI: EditorAPI;
}) {
  const {
    isInInteractionMode,
    isShownOnlyOnVariant,
    getCompVariantPointer,
    isInteractionModeAvailable,
    getComponentCompVariantPointersIfExists,
    getCompPointerForInteraction,
    getInteractionName,
  } = interactionsSelectors;

  function shouldCascadeStylesToRef(compRef: CompRef) {
    const isCompHasStyleInMobile = () => {
      const pageMobileVariant = editorAPI.mobile.getMobileVariant();
      if (pageMobileVariant) {
        const compMobileVariantPointer =
          editorAPI.components.variants.getPointer(compRef, [
            pageMobileVariant,
          ]);
        return !!editorAPI.components.style.get(compMobileVariantPointer);
      }
    };
    return (
      editorAPI.components.refComponents.isReferredComponent(compRef) &&
      !editorAPI.isMobileEditor() &&
      isCompHasStyleInMobile()
    );
  }

  function cascadeStyleToMobileIfNeeded(
    compRef: CompRef,
    styleValue: CompStructure['style'],
    updateFn: (compPointer: CompRef, styleVal: CompStructure['style']) => void,
    currentCompStyle: CompStructure['style'],
  ) {
    if (shouldCascadeStylesToRef(compRef)) {
      updateStyleInMobile(compRef, styleValue, updateFn, currentCompStyle);
    }
  }

  const clearStCss = (styleValue: CompStructure['style']) => {
    const VALID_EMPTY_ST_CSS = '.root {}';
    const styleValueCopy: any = _.cloneDeep(styleValue);
    const styleProperties = _.get(styleValue, 'style.properties');
    if (styleProperties && styleProperties['$st-css']) {
      styleValueCopy.style.properties['$st-css'] = VALID_EMPTY_ST_CSS;
    }
    return styleValueCopy;
  };

  const getNewMobileStyle = (
    compMobileVariantPointer: CompRef,
    styleValue: CompStructure['style'],
    currentComponentDesktopStyle: CompStructure['style'],
    isMobileEditor: boolean,
  ) => {
    if (isMobileEditor) {
      return styleValue;
    }

    const styleValueCopy = _.cloneDeep(styleValue);
    const updatedStyleProperties = _.get(styleValue, 'style.properties');

    if (updatedStyleProperties && currentComponentDesktopStyle) {
      const currentComponentMobileStyle = editorAPI.components.style.get(
        compMobileVariantPointer,
      );
      if (!currentComponentMobileStyle) {
        return;
      }

      const currentCompDesktopStyleProperties = _.get(
        currentComponentDesktopStyle,
        'style.properties',
      );
      const currentCompMobileStyleProperties = _.get(
        currentComponentMobileStyle,
        'style.properties',
      );

      if (
        !currentCompDesktopStyleProperties ||
        !currentCompMobileStyleProperties
      ) {
        return;
      }

      const propertiesBeingUpdated = componentsUtils.getDiffObject(
        updatedStyleProperties,
        currentCompDesktopStyleProperties,
      );
      if (!propertiesBeingUpdated) {
        return;
      }
      const mobileAlreadyUsesDifferentValues = componentsUtils.getDiffObject(
        propertiesBeingUpdated,
        _.pick(
          currentCompMobileStyleProperties,
          Object.keys(propertiesBeingUpdated),
        ),
      );
      if (mobileAlreadyUsesDifferentValues) {
        return;
      }

      // style.properties contains other comp properties besides the one that is currently being updated. we do not need to update other mobile properties
      const updatedValues = _.pick(
        updatedStyleProperties,
        Object.keys(propertiesBeingUpdated),
      );

      Object.assign(currentCompMobileStyleProperties, updatedValues);
      Object.keys(propertiesBeingUpdated).forEach((key) => {
        if (updatedStyleProperties[key] === undefined) {
          delete currentCompMobileStyleProperties[key];
        }
      });
      Object.assign(
        currentComponentMobileStyle.style.properties,
        currentCompMobileStyleProperties,
      );
      return currentComponentMobileStyle;
    }
    return styleValueCopy;
  };

  const getMobileStyleValue = (
    compRef: CompRef,
    styleValue: CompStructure['style'],
    compMobileVariantPointer: any,
    currentCompStyle: CompStructure['style'],
  ) => {
    const STYLESHEET_KEY_PATH = ['style', 'properties', '$st-css'];
    const isStylable = !!_.get(styleValue, STYLESHEET_KEY_PATH);
    const variantStyleItem = editorAPI.components.style.get(
      compMobileVariantPointer,
    );
    const isNewVariant = !variantStyleItem;
    const shouldClearStCss =
      isStylable && isNewVariant && !hasResponsiveLayout(editorAPI, compRef);
    const newMobileStyle = getNewMobileStyle(
      compMobileVariantPointer,
      styleValue,
      currentCompStyle,
      editorAPI.isMobileEditor(),
    );

    return shouldClearStCss && newMobileStyle
      ? clearStCss(newMobileStyle)
      : newMobileStyle;
  };

  function updateStyleInMobile(
    compRef: CompRef,
    styleValue: CompStructure['style'],
    updateFn: (compPointer: CompRef, styleVal: CompStructure['style']) => void,
    currentCompStyle?: CompStructure['style'],
  ) {
    const mobileVariant = editorAPI.mobile.getMobileVariant();
    const compMobileVariantPointer = editorAPI.components.variants.getPointer(
      compRef,
      [mobileVariant],
    );

    const mobileStyleValue = getMobileStyleValue(
      compRef,
      styleValue,
      compMobileVariantPointer,
      currentCompStyle,
    );
    if (mobileStyleValue) {
      updateFn(compMobileVariantPointer, mobileStyleValue);
    }
  }

  function getStyle(compRefs: CompRef[]) {
    const dsRead: DSRead = editorAPI.dsRead;
    const components = arrayUtils.asArray(compRefs);
    if (arrayUtils.isMultiselect(components)) {
      throw new Error("components.style.get isn't supported for multi select");
    }
    const [comp] = components;
    if (
      editorAPI.isMobileEditor() &&
      !editorAPI.mobile.mobileOnlyComponents.isMobileOnlyNonNativeComponent(
        comp,
      )
    ) {
      const pageMobileVariant = editorAPI.mobile.getMobileVariant();
      const compMobileVariantPointer = editorAPI.components.variants.getPointer(
        comp,
        [pageMobileVariant],
      );
      return (
        dsRead.components.style.get(compMobileVariantPointer) ||
        dsRead.components.style.get(comp)
      );
    }
    const currentEditorState = editorAPI.store.getState();
    if (
      isInInteractionMode(currentEditorState) &&
      !isShownOnlyOnVariant(editorAPI, components[0])
    ) {
      if (experiment.isOpen('se_decoupleStyleApiFromInteractionSelectors')) {
        const compRef = components[0];
        const compPointer = getCompPointerForInteraction(
          editorAPI,
          compRef,
          getInteractionName(currentEditorState),
        );

        return (
          dsRead.components.style.get(compPointer) ||
          dsRead.components.style.get(compRef)
        );
      }

      const pointerWithVariant = getCompVariantPointer(currentEditorState);
      const compRef =
        selectionSelectors.getSelectedCompsRefs(currentEditorState)[0]; // components[0] can be compVariantRef
      return (
        dsRead.components.style.get(pointerWithVariant) ||
        dsRead.components.style.get(compRef)
      );
    }

    return dsRead.components.style.get(components[0]);
  }

  function isSkinChange(compRef: CompRef, styleValue: CompStructure['style']) {
    const { skin: oldSkin } = editorAPI.components.style.get(compRef) || {};
    const { skin: newSkin } = styleValue as StyleRefOrStyleRefs;

    return oldSkin && newSkin && oldSkin !== newSkin;
  }

  function updateStyle(
    compRefs: CompRef | CompRef[],
    styleValue: CompStructure['style'],
    dontAddToUndoRedoStack?: boolean,
    dontRemoveScopedStyle?: boolean,
    removeScopedStyle?: boolean,
  ) {
    const components = arrayUtils.asArray(compRefs);
    if (arrayUtils.isMultiselect(components)) {
      throw new Error(
        "components.style.update isn't supported for multi select",
      );
    }

    let res;
    const currentEditorState = editorAPI.store.getState();
    const isInteractionMode = isInInteractionMode(currentEditorState);

    const shouldRemoveScopedStyle =
      removeScopedStyle || isSkinChange(components[0], styleValue);

    if (
      // @ts-expect-error
      isInteractionModeAvailable(currentEditorState) &&
      !dontRemoveScopedStyle &&
      !isInteractionMode &&
      shouldRemoveScopedStyle
    ) {
      const compVariantPointers: CompVariantPointer[] =
        getComponentCompVariantPointersIfExists(editorAPI, components[0]);

      const pageMobileVariant = editorAPI.mobile.getMobileVariant();

      if (!_.isEmpty(pageMobileVariant)) {
        const compMobileVariantPointer =
          editorAPI.components.variants.getPointer(components[0], [
            pageMobileVariant,
          ]);
        compVariantPointers.push(compMobileVariantPointer);
      }

      compVariantPointers.forEach((compWithVariant) => {
        if (editorAPI.components.style.get(compWithVariant)) {
          editorAPI.components.style.scoped.remove(compWithVariant);
        }
      });
    }

    if (isInteractionMode && !isShownOnlyOnVariant(editorAPI, components[0])) {
      const pointerWithVariant = experiment.isOpen(
        'se_decoupleStyleApiFromInteractionSelectors',
      )
        ? getCompPointerForInteraction(
            editorAPI,
            components[0],
            getInteractionName(currentEditorState),
          )
        : getCompVariantPointer(currentEditorState);
      const { id, ...styleValueWithoutId } = styleValue as StyleRefOrStyleRefs;
      res = editorAPI.dsActions.components.style.update(
        pointerWithVariant,
        // @ts-expect-error
        styleValueWithoutId,
      );
      interactionsActions.setDefaultTransition(editorAPI, components[0]);
    } else if (
      !shouldRemoveScopedStyle &&
      editorAPI.isMobileEditor() &&
      !editorAPI.mobile.mobileOnlyComponents.isMobileOnlyComponent(
        components[0]?.id,
      )
    ) {
      updateStyleInMobile(
        components[0],
        styleValue,
        // @ts-expect-error
        editorAPI.dsActions.components.style.update,
      );
    } else {
      const compRef = components[0];
      const currentCompStyle = editorAPI.components.style.get(compRef);
      // @ts-expect-error
      res = editorAPI.dsActions.components.style.update(compRef, styleValue);
      cascadeStyleToMobileIfNeeded(
        compRef,
        styleValue,
        // @ts-expect-error
        editorAPI.dsActions.components.style.update,
        currentCompStyle,
      );

      updateVariantsStyleIfNeeded(compRef, styleValue as StyleRef);
    }

    editorAPI.dsActions.platform.notifyAppsOnCustomEvent(
      platformEvents.factory.componentStyleChanged({ compRef: components[0] }),
    );

    if (!dontAddToUndoRedoStack) {
      editorAPI.history.debouncedAdd('Update component style');
    }
    componentsUtils.sendBiIfRefComponent(
      editorAPI,
      components[0],
      componentsUtils.REF_COMPONENT_CHANGE_TYPE.STYLE,
    );
    return res;
  }

  function updateVariantsStyleIfNeeded(compRef: CompRef, styleValue: StyleRef) {
    if (!editorAPI.components.refComponents.isReferredComponent(compRef)) {
      return;
    }

    const { State: variantPointer } =
      editorAPI.components.variants.getAllAffectingVariantsGroupedByVariantType(
        compRef,
      );

    if (!variantPointer) {
      return;
    }

    const compVariantPointer = editorAPI.components.variants.getPointer(
      compRef,
      variantPointer,
    );
    const styleValueInVariant =
      editorAPI.components.style.get(compVariantPointer);

    if (!styleValueInVariant) {
      return;
    }

    const variantStyleProperties = _.get(
      styleValueInVariant,
      'style.properties',
    );
    const newStyleProperties = _.get(styleValue, 'style.properties');
    const omitBgProperties = ['bg', 'brd', 'alpha-bg', 'alpha-brd'];

    const bordersInVariant = _.omit(variantStyleProperties, omitBgProperties);
    const newBorders = _.omit(newStyleProperties, omitBgProperties);

    const propertiesBeingUpdated = componentsUtils.getDiffObject(
      bordersInVariant,
      newBorders,
    );

    if (!propertiesBeingUpdated) {
      return;
    }

    const updatedVariantStyleProperties = {
      ...variantStyleProperties,
      ...propertiesBeingUpdated,
    };

    _.set(
      styleValueInVariant,
      'style.properties',
      updatedVariantStyleProperties,
    );

    editorAPI.dsActions.components.style.update(
      compVariantPointer,
      styleValueInVariant,
    );
  }

  function forkStyle(compRefs: CompRef[], styleValue: AnyFixMe) {
    const components = arrayUtils.asArray(compRefs);
    if (arrayUtils.isMultiselect(components)) {
      throw new Error(
        "components.style.forkStyle isn't supported for multi select",
      );
    }

    // @ts-expect-error
    return editorAPI.dsActions.components.style.fork(components[0], styleValue);
  }

  return {
    get: getStyle,
    update: updateStyle,
    fork: forkStyle,
    updateStyleInMobile,
    cascadeStyleToMobileIfNeeded,
  };
}

export type ComponentsStyleApi = ReturnType<typeof createComponentsStyleApi>;
